import type { JsonSchema, JsonSchemaTypeName, SchemaWidget, UiSchema } from './SchemaForm.types';

const NULL_TYPE: JsonSchemaTypeName = 'null';

/**
 * `z.toJSONSchema()` renders `.nullable()` as `anyOf: [<type>, {type: 'null'}]`.
 * Collapsing that back to the single non-null member is what lets a nullable
 * property render as an ordinary control instead of an unsupported union.
 */
export function unwrapNullable(schema: JsonSchema): JsonSchema {
  const members = schema.anyOf;
  if (!members || members.length !== 2) return schema;

  const nonNull = members.filter((member) => member.type !== NULL_TYPE);
  if (nonNull.length !== 1) return schema;

  const rest = { ...schema };
  delete rest.anyOf;
  return { ...nonNull[0], ...rest };
}

export function isNullable(schema: JsonSchema): boolean {
  if (Array.isArray(schema.type)) return schema.type.includes(NULL_TYPE);
  return (schema.anyOf ?? []).some((member) => member.type === NULL_TYPE);
}

export function getTypeName(schema: JsonSchema): JsonSchemaTypeName | undefined {
  const { type } = schema;
  if (Array.isArray(type)) return type.find((name) => name !== NULL_TYPE);
  return type;
}

/** Every keyword whose presence means the schema is outside the supported subset. */
function hasUnmodelledCombinator(schema: JsonSchema): boolean {
  return (
    schema.$ref !== undefined ||
    schema.const !== undefined ||
    schema.oneOf !== undefined ||
    schema.allOf !== undefined ||
    schema.anyOf !== undefined
  );
}

export type FieldKind =
  | 'widget'
  | 'enum'
  | 'text'
  | 'textarea'
  | 'number'
  | 'boolean'
  | 'array'
  | 'object'
  | 'map'
  | 'unsupported';

export function getWidgetName(uiSchema: UiSchema | undefined): string | undefined {
  const widget = uiSchema?.['ui:widget'];
  return typeof widget === 'string' ? widget : undefined;
}

export function getFieldKind(
  schema: JsonSchema,
  uiSchema: UiSchema | undefined,
  widgets: Record<string, SchemaWidget>,
): FieldKind {
  const widgetName = getWidgetName(uiSchema);
  if (widgetName !== undefined && widgetName in widgets) return 'widget';
  if (widgetName === 'textarea') return 'textarea';

  const resolved = unwrapNullable(schema);
  if (hasUnmodelledCombinator(resolved)) return 'unsupported';
  if (resolved.enum !== undefined) return 'enum';

  switch (getTypeName(resolved)) {
    case 'string':
      return 'text';
    case 'number':
    case 'integer':
      return 'number';
    case 'boolean':
      return 'boolean';
    case 'array':
      return resolved.items === undefined ? 'unsupported' : 'array';
    case 'object':
      if (resolved.properties !== undefined) return 'object';
      return typeof resolved.additionalProperties === 'object' ? 'map' : 'unsupported';
    default:
      return 'unsupported';
  }
}

/**
 * Field labels come from the schema, so they are data rather than product copy.
 * The property key is the fallback, matching what the previous form displayed.
 */
export function getFieldLabel(schema: JsonSchema, name: string): string {
  return schema.title ?? name;
}

export function joinPath(parent: string, segment: string | number): string {
  return parent === '' ? String(segment) : `${parent}.${segment}`;
}

/** Turns a path into a value safe for a DOM id. */
export function pathToId(prefix: string, path: string): string {
  return `${prefix}-${path.replace(/[^a-zA-Z0-9-]/g, '_')}`;
}

/**
 * A schema `default` is shown when nothing is stored yet, but it is not written
 * into the form data: only an edit the person actually makes reaches the payload.
 */
export function withDefault(schema: JsonSchema, value: unknown): unknown {
  return value === undefined ? schema.default : value;
}

/** The value a freshly added array entry starts from. */
export function emptyValueFor(schema: JsonSchema): unknown {
  const resolved = unwrapNullable(schema);
  switch (getTypeName(resolved)) {
    case 'object':
      return {};
    case 'array':
      return [];
    case 'boolean':
      return false;
    case 'number':
    case 'integer':
      return undefined;
    default:
      return '';
  }
}

export function getUiSchemaFor(uiSchema: UiSchema | undefined, key: string): UiSchema | undefined {
  const child = uiSchema?.[key];
  return typeof child === 'object' ? child : undefined;
}
